import React, {useEffect, useMemo, useRef} from 'react';
import clsx from 'clsx';
import {Alert} from '@netx/core-components/Alert';
import {ErrorField} from '@netx/core-components/ErrorField';
import {getURLFormattedText, isEmptyValue} from '@netx/core-components/Form';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {i18n} from '@netx/core/i18n';
import {ATTRIBUTE_APPENDABLE_TYPES} from '../../constants';
import type {IAttributeTemplate} from '../../schema';
import type {AttributeAction, AttributeValue} from '../../types';
import {getAttributeComponent} from '../../utils/getAttributeComponent';
import ClearToggle from './components/Action/ClearToggle';
import {InputAdornment as MetadataLookupAdornment} from './components/MetadataLookup';
import {
	EDITABLE_ATTRIBUTE_CLASS_NAME,
	editableAttributeClasses,
	EditableAttributeClasses,
	getEditableAttributeUtilityClass,
} from './editableAttributeClasses';

type EditableAttributeOwnerState = WithClasses<
	EditableAttributeClasses,
	{
		align?: 'top' | 'bottom' | 'center';
		appending?: boolean;
		limitHeightToContainer?: boolean;
		matchContainerHeight?: boolean;
		multipleValues?: boolean;
	}
>;

const useUtilityClasses = (ownerState: EditableAttributeOwnerState) => {
	const {
		align,
		appending,
		classes,
		limitHeightToContainer,
		multipleValues,
		matchContainerHeight,
	} = ownerState;
	const slots = {
		root: [
			'root',
			align && `align-${align}`,
			appending && 'appending',
			limitHeightToContainer && 'limitHeightToContainer',
			matchContainerHeight && 'matchContainerHeight',
			multipleValues && 'multipleValues',
		],
		inputWrapper: ['inputWrapper'],
		input: ['input'],
		trailingControls: ['trailingControls'],
	};
	return composeClasses(slots, getEditableAttributeUtilityClass, classes);
};

const EditableAtteibuteRoot = styled('div', {
	name: EDITABLE_ATTRIBUTE_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {appending, limitHeightToContainer, matchContainerHeight} =
		ownerState as EditableAttributeOwnerState;
	return {
		alignItems: 'flex-start',
		display: 'inline-flex',
		flex: '1 1 auto',
		flexDirection: 'column',
		minWidth: 0,
		width: '100%',
		margin: theme.spacing(1, 0),
		// Call out fields that are appendable
		// (non appendable fields will be disabled)
		...(appending
			? {
					'&[data-nx-appendable="true"] .MuiInputBase-root': {
						// TODO: this only accounts for outline variant
						'& [class*="MuiOutlinedInput-notchedOutline"]': {
							borderColor: theme.palette.primary.main,
						},
					},
				}
			: null),
		...(matchContainerHeight
			? {
					margin: 0,
					[`& .${editableAttributeClasses.input}`]: {
						height: '100%',
					},
					'& textarea': {
						height: '100% !important',
					},
				}
			: null),
		...(limitHeightToContainer
			? {
					height: '100%',
					'& textarea': {
						height: 'auto !important',
						maxHeight: '100%',
						overflow: 'auto !important',
					},
					[`& .${editableAttributeClasses.inputWrapper}`]: {
						height: '100%',
					},
					[`& .${editableAttributeClasses.input}`]: {
						maxHeight: '100%',
					},
					'& .MuiAutocomplete-root': {
						height: '100%',
						maxHeight: '100%',
					},
					'& .MuiFormControl-root': {
						height: '100%',
						maxHeight: '100%',
					},
					'& .MuiInputBase-root': {
						overflow: 'auto',
						position: 'static',
						height: '100%',
					},
					'& fieldset': {
						position: 'absolute',
						top: -5,
						bottom: -1,
						left: 0,
						right: 0,
					},
				}
			: null),
	};
});

const EditableAtteibuteTrailingControls = styled('div', {
	name: EDITABLE_ATTRIBUTE_CLASS_NAME,
	slot: 'TrailingControls',
})(({theme}) => ({
	display: 'inline-flex',
	flex: '0 0 auto',
	height: 'max-content',
	marginRight: -4,
	marginLeft: 4,
	'& > *': {
		marginLeft: theme.spacing(1),
	},
}));

const EditableAttributeInputWrapper = styled('div', {
	name: EDITABLE_ATTRIBUTE_CLASS_NAME,
	slot: 'InputWrapper',
})(({theme, ownerState}) => {
	const {align} = ownerState as EditableAttributeOwnerState;
	return {
		alignItems: 'flex-start',
		display: 'inline-flex',
		width: '100%',
		...(!align || align === 'top'
			? null
			: align === 'bottom'
				? {
						alignItems: 'flex-end',
					}
				: {
						alignItems: 'center',
					}),
	};
});

const EditableAttributeInput = styled('div', {
	name: EDITABLE_ATTRIBUTE_CLASS_NAME,
	slot: 'Input',
})(({theme}) => ({
	alignSelf: 'center',
	display: 'inline-flex',
	flex: '1 1 auto',
	minWidth: 0,
}));

export type AttributeEvent = {
	attribute: IAttributeTemplate;
	value: AttributeValue;
	action: AttributeAction;
};

export interface EditableAttributeProps extends WithClasses<EditableAttributeClasses> {
	action?: AttributeAction;
	alignActions?: 'top' | 'bottom' | 'center';
	appendable?: boolean;
	appending?: boolean;
	attrData: IAttributeTemplate;
	attributeWorking?: boolean;
	changed?: boolean;
	clearable?: boolean;
	disabled?: boolean;
	disableFolksonomy?: boolean;
	enforceMandatory?: boolean;
	alwaysShowRequiredWhenMandatory?: boolean;
	error?: string;
	forceClearable?: boolean;
	helperText?: React.ReactNode;
	hideLabel?: boolean;
	required?: boolean;
	/** override attribute name label */
	label?: string;
	loadingValue?: boolean;
	limitHeightToContainer?: boolean;
	matchContainerHeight?: boolean;
	inputRef?: React.Ref<any>;
	isMetadataLookup?: boolean;
	isMetadataTarget?: boolean;
	multipleValues?: boolean;
	name?: string;
	placeholder?: string;
	value?: any;
	onLookup?: (
		attribute: IAttributeTemplate,
		lookupValue: string,
	) => Promise<any> | undefined;
	onSearch?: (value: string) => void;
	onActionChange?: (action: AttributeAction) => void;
	onValueChange?: (value: AttributeValue, action: AttributeAction) => void;
}

export const EditableAttributePrimitive = (
	{
		classes: _classes,
		className,
		alignActions = 'top',
		appending,
		appendable,
		action = 'overwrite',
		changed,
		clearable,
		disableFolksonomy,
		enforceMandatory = true,
		forceClearable,
		attrData,
		attributeWorking,
		disabled,
		error,
		helperText,
		hideLabel,
		inputRef,
		label,
		alwaysShowRequiredWhenMandatory,
		isMetadataLookup,
		isMetadataTarget,
		limitHeightToContainer,
		loadingValue,
		matchContainerHeight,
		multipleValues,
		name,
		required,
		onLookup,
		onSearch,
		onValueChange,
		onActionChange,
		placeholder,
		value = null,
	}: EditableAttributeProps,
	ref: any,
) => {
	const lastNonClearAction = useRef(
		action === 'clear' ? 'overwrite' : (action ?? 'overwrite'),
	);

	useEffect(() => {
		lastNonClearAction.current =
			action === 'clear'
				? lastNonClearAction.current
				: (action ?? lastNonClearAction.current);
	}, [action]);

	const ownerState: EditableAttributeOwnerState = {
		align: alignActions,
		appending: action === 'append' || appending,
		className,
		limitHeightToContainer,
		matchContainerHeight,
		// TODO: clean up this class name to reflect that we are showing a placeholder as a value
		multipleValues: multipleValues || action === 'clear',
	};

	const classes = useUtilityClasses(ownerState);

	const attributeName = useMemo(() => {
		return label != null
			? label
			: // TODO: handle this before we get here via the attributes context methods
				attrData.system
				? i18n.t(`i18n.${attrData.name}`)
				: attrData.name;
	}, [label, attrData.system, attrData.name]);

	const fieldName = name || `${attrData.attributeid}`;

	const showRequired =
		required ??
		(attrData.mandatory === 1 &&
			(enforceMandatory || alwaysShowRequiredWhenMandatory));

	const canAppend =
		appendable && ATTRIBUTE_APPENDABLE_TYPES.includes(attrData.type);

	const _placeholder =
		placeholder != null
			? placeholder
			: action === 'clear'
				? i18n.t('i18n.tagPanelEmptiedValue')
				: undefined;

	// Will be sent to attribute component
	const sendProps = {
		action,
		attrData: {
			...attrData,
			name: attributeName,
		},
		changed,
		disabled:
			attributeWorking ||
			disabled ||
			((appending || action === 'append') && !canAppend),
		disableFolksonomy,
		error,
		helperText: getURLFormattedText(helperText),
		hideLabel,
		inputRef,
		isMetadataLookup,
		isMetadataTarget,
		loadingValue,
		multipleValues,
		name: fieldName,
		placeholder: _placeholder,
		required: showRequired,
		value: value as any,
		onSearch,
	};

	const disabledActions: AttributeAction[] = [];
	if (!clearable || (!forceClearable && attrData.mandatory === 1)) {
		disabledActions.push('clear');
	}

	useEffect(() => {
		if (disabledActions.includes(action)) {
			onActionChange?.(lastNonClearAction.current);
		}
	}, [disabledActions, action]);

	/**
	 * Set attribute action
	 * A helper so you do not need to keep proving `attrData.name`
	 */
	const setAction = useConstCallback((action: AttributeAction) => {
		onActionChange?.(action);
	});

	/** clear toggle */
	const handleClearToggle = useConstCallback(
		(
			event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
			cleared: boolean,
		) => {
			setAction(cleared ? 'clear' : lastNonClearAction.current);
		},
	);

	/** metadata lookup */
	const handleLookup = useConstCallback((lookupValue: string) =>
		onLookup?.(attrData, lookupValue),
	);

	/** update value */
	const handleValueChange = useConstCallback(
		(event: any, newValue: AttributeValue) => {
			if (isEmptyValue(newValue)) newValue = null;
			onValueChange?.(newValue, action);
		},
	);

	const AttributeComponent = getAttributeComponent(attrData.type);
	if (!AttributeComponent) {
		return (
			<Alert severity="error">
				<>
					Error finding component for attribute type{' '}
					{attrData.type ?? `for ${attrData.name}`}
				</>
			</Alert>
		);
	}

	return (
		<EditableAtteibuteRoot
			data-nx-appendable={canAppend}
			className={clsx(classes.root, className)}
			ownerState={ownerState}
		>
			<EditableAttributeInputWrapper
				className={classes.inputWrapper}
				ownerState={ownerState}
			>
				<EditableAttributeInput className={classes.input}>
					{isMetadataLookup || isMetadataTarget ? (
						<MetadataLookupAdornment
							attrData={attrData}
							attributeWorking={attributeWorking}
							disabled={disabled}
							isMetadataLookup={isMetadataLookup}
							isMetadataTarget={isMetadataTarget}
						/>
					) : null}

					<AttributeComponent
						{...sendProps}
						onChange={handleValueChange}
						onLookup={handleLookup}
					/>
				</EditableAttributeInput>

				{clearable || forceClearable ? (
					<EditableAtteibuteTrailingControls
						className={classes.trailingControls}
					>
						<ClearToggle
							disabled={disabled || disabledActions.includes('clear')}
							on={action === 'clear' && !disabledActions.includes('clear')}
							onToggle={handleClearToggle}
						/>
					</EditableAtteibuteTrailingControls>
				) : null}
			</EditableAttributeInputWrapper>

			{error != null ? <ErrorField disabled={disabled} error={error} /> : null}
		</EditableAtteibuteRoot>
	);
};

export const EditableAttribute = React.forwardRef(EditableAttributePrimitive);

export default EditableAttribute;
